import React, { createContext, useContext, useEffect, useState } from 'react';

/* ─────────────────────────────────────────────────────────────
   ctAgency Suite™ theme system — 11 named themes selected by a
   single `data-theme` attribute and persisted to localStorage
   under 'pnb-theme'. Dark-family themes also toggle the `.dark`
   class as a bridge for legacy `dark:` raw utilities.
───────────────────────────────────────────────────────────── */

export type ThemeId =
  | 'light'
  | 'light-blue'
  | 'high-contrast'
  | 'high-contrast-dark'
  | 'warm'
  | 'dark'
  | 'dark-blue'
  | 'deuteranopia'
  | 'protanopia'
  | 'tritanopia'
  | 'monochrome';

export type ThemeCategory = 'Standard' | 'Dark' | 'Accessibility' | 'Color blind';

export interface ThemeMeta {
  id: ThemeId;
  label: string;
  category: ThemeCategory;
  /** swatch hex shown in the picker */
  swatch: string;
}

export const THEMES: ThemeMeta[] = [
  { id: 'light',              label: 'Light',               category: 'Standard',      swatch: '#0369a1' },
  { id: 'light-blue',         label: 'Light Blue',          category: 'Standard',      swatch: '#1d4ed8' },
  { id: 'warm',               label: 'Warm Light',          category: 'Standard',      swatch: '#b45309' },
  { id: 'dark',               label: 'Dark',                category: 'Dark',          swatch: '#60a5fa' },
  { id: 'dark-blue',          label: 'Dark Blue',           category: 'Dark',          swatch: '#3b82f6' },
  { id: 'high-contrast',      label: 'High Contrast',       category: 'Accessibility', swatch: '#0c4a6e' },
  { id: 'high-contrast-dark', label: 'High Contrast Dark',  category: 'Accessibility', swatch: '#6cb6ff' },
  { id: 'monochrome',         label: 'Monochrome',          category: 'Accessibility', swatch: '#525252' },
  { id: 'deuteranopia',       label: 'Deuteranopia',        category: 'Color blind',   swatch: '#0050ef' },
  { id: 'protanopia',         label: 'Protanopia',          category: 'Color blind',   swatch: '#0050ef' },
  { id: 'tritanopia',         label: 'Tritanopia',          category: 'Color blind',   swatch: '#c2185b' },
];

const DARK_FAMILY: ThemeId[] = ['dark', 'dark-blue', 'high-contrast-dark'];
const VALID = new Set<ThemeId>(THEMES.map(t => t.id));
const STORAGE_KEY = 'pnb-theme';

interface ThemeContextValue {
  theme: ThemeId;
  themes: ThemeMeta[];
  setTheme: (t: ThemeId) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}

/** Resolve the stored theme, migrating the legacy color+mode keys once. */
export function readStoredTheme(): ThemeId {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && VALID.has(stored as ThemeId)) return stored as ThemeId;

  // Migrate legacy 'theme-color' + 'theme-mode'
  const mode = localStorage.getItem('theme-mode');
  const prefersDark =
    mode === 'dark' ||
    (mode === 'system' && window.matchMedia?.('(prefers-color-scheme: dark)').matches) ||
    (!mode && window.matchMedia?.('(prefers-color-scheme: dark)').matches);
  return prefersDark ? 'dark' : 'light';
}

export function applyTheme(theme: ThemeId): void {
  const html = document.documentElement;
  html.setAttribute('data-theme', theme);
  html.classList.toggle('dark', DARK_FAMILY.includes(theme));
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<ThemeId>(() => {
    const t = readStoredTheme();
    applyTheme(t);
    return t;
  });

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = (t: ThemeId) => {
    localStorage.setItem(STORAGE_KEY, t);
    setThemeState(t);
  };

  return (
    <ThemeContext.Provider value={{ theme, themes: THEMES, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}
